import { usersService } from "@/app/services/usersService";
import {
  PartnerCommissionConfig,
  userUpdateData,
} from "@/app/services/usersService/userUpdate";
import { PartnerType } from "@/app/utils/partners.enum";
import { useToast } from "@/view/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useDefaultGoals } from "@/app/hooks/useDefaultGoals";
import { tableService } from "@/app/services/tableService";
import masks from "@/app/utils/masks";

const formValidation = z.record(z.string(), z.string().optional());

type formData = z.infer<typeof formValidation>;

export const useEditCommissionModalController = ({
  onClose,
  open,
  sellerId,
  commissions,
}: {
  onClose: () => void;
  open: boolean;
  sellerId: string;
  commissions?: Record<PartnerType, PartnerCommissionConfig>;
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { tables } = useDefaultGoals();

  const { data: cltTablesData } = useQuery({
    queryKey: ["allTables", { type: "CLT", limit: 99 }],
    queryFn: () => tableService.table({ type: "CLT", limit: 99 }),
  });

  const getMergedTables = () => {
    const baseTables = tables?.pages?.[0]?.tables || [];
    const cltTables = cltTablesData?.tables || [];
    return [
      ...baseTables,
      ...cltTables.filter(
        (clt) => !baseTables.some((t: any) => t._id === clt._id),
      ),
    ];
  };

  const { mutateAsync: updateMutate } = useMutation({
    mutationFn: ({ id, body }: { id: string; body: userUpdateData }) =>
      usersService.userUpdate(body, id),
    onError: (e: any) => {
      toast({
        variant: "destructive",
        title: e?.message || "Erro ao atualizar. Tente novamente mais tarde.",
      });
    },
    onSuccess: (_, { id }) => {
      toast({
        variant: "default",
        title: "Comissões atualizadas com sucesso!",
      });

      queryClient.invalidateQueries({
        queryKey: ["sellerInfo", id],
      });
    },
  });

  const form = useForm<formData>({
    resolver: zodResolver(formValidation),
  });

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = form;

  const onSubmit = async (data: formData) => {
    if (!sellerId) return;

    const allTables = getMergedTables();
    const newCommissions: Record<PartnerType, PartnerCommissionConfig> =
      {} as any;

    Object.keys(data).forEach((key) => {
      const valueStr = data[key];
      if (!valueStr) return;

      let cleanStr = valueStr.replace("%", "").replace("R$", "").trim();

      if (cleanStr.includes(",")) {
        cleanStr = cleanStr.replace(/\./g, "").replace(",", ".");
      }

      let value = Number(cleanStr);

      if (isNaN(value)) return;

      const parts = key.split("_");
      if (parts.length < 3) return;

      const partner = parts[0] as PartnerType;
      const tableId = parts[1];
      const identifier = parts[2];

      const isMoney = [PartnerType.SALVCAR, PartnerType.CREFISA].includes(
        partner,
      );

      if (isMoney) {
        value = Math.round(value * 100);
      }

      const isFgtsLike =
        partner === PartnerType.FGTS || partner === PartnerType.CLT;

      if (!newCommissions[partner]) {
        newCommissions[partner] = {
          _id: !isFgtsLike ? tableId : undefined,
          items: [],
        };
      }

      if (isFgtsLike) {
        if (identifier === "supervisor") {
          newCommissions[partner].items.push({
            _id: tableId,
            supervisorCommission: value,
          });
        }
      } else {
        if (newCommissions[partner]._id !== tableId) {
          newCommissions[partner]._id = tableId;
        }

        const table = allTables.find((t: any) => t._id === tableId);
        if (table && table.valuesAndCommissions) {
          const index = parseInt(identifier);
          if (!isNaN(index) && table.valuesAndCommissions[index]) {
            const rangeValue = table.valuesAndCommissions[index].value;
            newCommissions[partner].items.push({
              value: rangeValue,
              supervisorCommission: value,
            });
          }
        } else if (identifier === "supervisor") {
          newCommissions[partner].items.push({
            _id: tableId,
            supervisorCommission: value,
          });
        }
      }
    });

    if (commissions) {
      Object.entries(commissions).forEach(([key, config]) => {
        const partner = key.toUpperCase() as PartnerType;
        if (!newCommissions[partner] && config && config.items) {
          newCommissions[partner] = config;
        }
      });
    }

    try {
      await updateMutate({
        id: sellerId,
        body: { commissionsV2: newCommissions },
      });
      onClose();
      reset();
    } catch (e) {
      console.error("Erro ao atualizar:", e);
    }
  };

  const percentageFormatValue = (value: number | string) => {
    const numeric = Number(String(value).replace(",", "."));
    if (isNaN(numeric)) return "0,00%";
    return `${numeric.toFixed(2)}%`;
  };

  useEffect(() => {
    if (open && tables) {
      const allTables = getMergedTables();
      const defaultValues: Record<string, string> = {};

      if (commissions) {
        Object.entries(commissions).forEach(([partnerKey, config]) => {
          const partner = partnerKey.toUpperCase() as PartnerType;
          if (!config || !config.items) return;

          const isFgtsLike =
            partner === PartnerType.FGTS || partner === PartnerType.CLT;
          const isMoney = [PartnerType.SALVCAR, PartnerType.CREFISA].includes(
            partner,
          );
          const isSimplePercent =
            partner === PartnerType.PAN || partner === PartnerType.CREFISA;

          if (isFgtsLike) {
            config.items.forEach((item) => {
              if (item._id && item.supervisorCommission !== undefined) {
                const key = `${partner}_${item._id}_supervisor`;
                defaultValues[key] = percentageFormatValue(
                  item.supervisorCommission,
                );
              }
            });
          } else {
            const tableId = config._id;
            const table = allTables.find((t: any) => t._id === tableId);

            if (table) {
              config.items.forEach((item) => {
                if (table.valuesAndCommissions) {
                  const index = table.valuesAndCommissions.findIndex(
                    (vc: any) => vc.value === item.value,
                  );
                  if (index !== -1 && item.supervisorCommission !== undefined) {
                    const key = `${partner}_${tableId}_${index}`;
                    defaultValues[key] = isMoney
                      ? masks.currency(item.supervisorCommission)
                      : percentageFormatValue(item.supervisorCommission);
                  }
                } else {
                  if (item.supervisorCommission !== undefined) {
                    const key = `${partner}_${tableId}_supervisor`;
                    defaultValues[key] = percentageFormatValue(
                      item.supervisorCommission,
                    );
                  }
                }
              });
            } else {
              if (isSimplePercent) {
                const item = config.items.find(
                  (i) => i.supervisorCommission !== undefined,
                );
                if (item?.supervisorCommission !== undefined) {
                  const key = `${partner}_${tableId}_percents`;
                  defaultValues[key] = percentageFormatValue(
                    item.supervisorCommission,
                  );
                }
              } else {
                config.items.forEach((item, index) => {
                  if (item.supervisorCommission !== undefined) {
                    const key = `${partner}_${tableId}_${index}`;
                    defaultValues[key] = isMoney
                      ? masks.currency(item.supervisorCommission)
                      : percentageFormatValue(item.supervisorCommission);
                  }
                });
              }
            }
          }
        });
      }

      reset(defaultValues);
    }
  }, [open, commissions, tables, reset]);

  return {
    form,
    errors,
    register,
    isSubmitting,
    handleSubmit,
    onSubmit,
  };
};
